/**
 * CalendarStats Component
 *
 * Displays statistics about posts in the calendar view
 */

import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";

function CalendarStats({ posts }) {
  const totalPosts = posts.length;
  const postedCount = posts.filter((p) => p.status === "Posted").length;
  const pendingCount = posts.filter(
    (p) => p.status === "Pending Approval" || p.status === "Good to go"
  ).length;
  const rejectedCount = posts.filter((p) => p.status === "Rejected").length;

  // Calculate posts by platform
  const postsWithLinkedIn = posts.filter((p) => p.linkedin_post_url || p.linkedinCopy).length;
  const postsWithX = posts.filter((p) => p.x_post_url || p.xCopy).length;
  const postsWithThreads = posts.filter((p) => p.threads_post_url || p.threadsCopy).length;

  const stats = [
    {
      icon: "article",
      label: "Total Posts",
      value: totalPosts,
      color: "info",
    },
    {
      icon: "check_circle",
      label: "Posted",
      value: postedCount,
      color: "success",
    },
    {
      icon: "pending",
      label: "Pending",
      value: pendingCount,
      color: "warning",
    },
    {
      icon: "cancel",
      label: "Rejected",
      value: rejectedCount,
      color: "error",
    },
  ];

  const platformStats = [
    {
      icon: "LinkedIn",
      label: "LinkedIn",
      value: postsWithLinkedIn,
      color: "primary",
    },
    {
      icon: "X",
      label: "X (Twitter)",
      value: postsWithX,
      color: "dark",
    },
    {
      icon: "forum",
      label: "Threads",
      value: postsWithThreads,
      color: "secondary",
    },
  ];

  return (
    <MDBox mb={3}>
      <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
        {/* Post Status Stats */}
        {stats.map((stat) => (
          <Grid item xs={6} sm={6} md={3} key={stat.label}>
            <Card
              sx={{
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                borderRadius: "12px",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: ({ boxShadows }) => boxShadows.lg,
                },
              }}
            >
              <MDBox p={{ xs: 2, sm: 2.5 }} display="flex" alignItems="center">
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width={{ xs: "2.5rem", sm: "3rem" }}
                  height={{ xs: "2.5rem", sm: "3rem" }}
                  borderRadius="md"
                  color="white"
                  bgColor={stat.color}
                  variant="gradient"
                  mr={{ xs: 1.5, sm: 2 }}
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  <Icon fontSize="medium" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
                    {stat.icon}
                  </Icon>
                </MDBox>
                <MDBox sx={{ minWidth: 0 }}>
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" } }}
                  >
                    {stat.value}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="medium"
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {stat.label}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        ))}

        {/* Platform Stats */}
        {platformStats.map((stat) => (
          <Grid item xs={12} sm={6} md={4} key={stat.label}>
            <Card
              sx={{
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                borderRadius: "12px",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: ({ boxShadows }) => boxShadows.lg,
                },
              }}
            >
              <MDBox p={{ xs: 2, sm: 2.5 }} display="flex" alignItems="center">
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width={{ xs: "2.25rem", sm: "2.5rem" }}
                  height={{ xs: "2.25rem", sm: "2.5rem" }}
                  borderRadius="md"
                  color="white"
                  bgColor={stat.color}
                  variant="gradient"
                  mr={{ xs: 1.5, sm: 2 }}
                  sx={{ flexShrink: 0 }}
                >
                  <MDTypography
                    variant="caption"
                    color="white"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                  >
                    {stat.icon}
                  </MDTypography>
                </MDBox>
                <MDBox sx={{ minWidth: 0 }}>
                  <MDTypography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
                  >
                    {stat.value}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="medium"
                    sx={{
                      fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {stat.label}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        ))}
      </Grid>
    </MDBox>
  );
}

CalendarStats.propTypes = {
  posts: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string.isRequired,
      linkedin_post_url: PropTypes.string,
      x_post_url: PropTypes.string,
      threads_post_url: PropTypes.string,
      linkedinCopy: PropTypes.string,
      xCopy: PropTypes.string,
      threadsCopy: PropTypes.string,
    })
  ).isRequired,
};

export default CalendarStats;
